/**
 * HUD — the always-visible round/trump/bid/score overlay. Owner: Phase-3 A3.1.
 *
 * Surfaces (PRD FR-G7, RULES §4.3):
 *   - Round n / N
 *   - Current trump suit (suit icon, color-cued)
 *   - Current bid value + contract team (bold once locked)
 *   - Tricks won for Team A and Team B
 *   - Break-even target = view.bidding.highBid.value (the contract bid)
 *   - Win target = break-even + 1 (RULES §4.3 — contract team needs B+1)
 *
 * The opposite team's targets are also surfaced compactly so all four numbers
 * (push/win for each team) are always within a glance — that's what
 * "tricks-needed always visible" means in PRD FR-G7.
 */
import { useLobby, useView } from "../state/hooks.js";

const SUIT_GLYPH: Record<"C" | "D" | "H" | "S", string> = {
  C: "♣",
  D: "♦",
  H: "♥",
  S: "♠",
};

const SUIT_NAME: Record<"C" | "D" | "H" | "S", string> = {
  C: "clubs",
  D: "diamonds",
  H: "hearts",
  S: "spades",
};

function suitClass(suit: "C" | "D" | "H" | "S"): string {
  return suit === "D" || suit === "H" ? "suit-red" : "suit-black";
}

export function HUD(): JSX.Element | null {
  const view = useView();
  const lobby = useLobby();
  if (!view) return null;

  const n = view.config.players;
  // RULES §3.1: a game = n rounds (4 rounds for 4p, 6 for 6p). Round index is
  // 0-based; humans expect 1-based display ("Round 2 / 4").
  const totalRounds = n;
  const currentRound = view.round + 1;

  const tricksA = view.tricksWon.A ?? 0;
  const tricksB = view.tricksWon.B ?? 0;

  const highBid = view.bidding.highBid;
  const contractTeam = view.bidding.contractTeam;
  const trump = view.trump;
  const T = view.rc.totalTricks;
  // Break-even target for the contract team is the bid value `B` (RULES §4.3).
  // The other team breaks even at `T - B`. Contract wins at `≥ B + 1`; other
  // team wins at `≥ T - B + 1`.
  const B = highBid?.value ?? null;

  const teamName = (t: "A" | "B"): string => `Team ${t}`;
  const contractLabel =
    contractTeam !== null && contractTeam !== undefined
      ? teamName(contractTeam)
      : "TBD";

  const callerSeat = view.callerSeat;
  const callerName =
    lobby?.seats.find((s) => s.seatIndex === callerSeat)?.displayName ??
    `seat ${callerSeat}`;

  return (
    <div className="hud" role="region" aria-label="Game status">
      <div className="hud-block hud-round" aria-label="Round">
        <span className="hud-label">Round</span>
        <span className="hud-value">
          {currentRound} <span className="hud-of">/ {totalRounds}</span>
        </span>
        <span className="hud-sub">
          Caller: <strong>{callerName}</strong>
        </span>
      </div>

      <div className="hud-block hud-trump" aria-label="Trump">
        <span className="hud-label">Trump</span>
        {trump ? (
          <span
            className={`hud-value hud-suit ${suitClass(trump)}`}
            aria-label={SUIT_NAME[trump]}
          >
            {SUIT_GLYPH[trump]}
          </span>
        ) : (
          <span className="hud-value hud-suit hud-suit-empty" aria-label="not set">
            —
          </span>
        )}
      </div>

      <div className="hud-block hud-bid" aria-label="Bid">
        <span className="hud-label">Bid</span>
        <span className="hud-value">
          {B !== null ? B : "—"}
          {highBid ? (
            <span
              className={`hud-value-suit hud-suit ${suitClass(highBid.trump)}`}
              aria-label={SUIT_NAME[highBid.trump]}
            >
              {SUIT_GLYPH[highBid.trump]}
            </span>
          ) : null}
        </span>
        <span className="hud-sub">Contract: <strong>{contractLabel}</strong></span>
      </div>

      <div className="hud-block hud-targets" aria-label="Targets" aria-live="polite" aria-atomic="true">
        <span className="hud-label">Targets</span>
        <span className="hud-value hud-targets-value">
          {B !== null ? (
            <>
              <span
                className={`hud-target ${contractTeam === "A" ? "is-contract" : ""}`}
              >
                A: push {contractTeam === "A" ? B : T - B} / win{" "}
                {contractTeam === "A" ? B + 1 : T - B + 1}
              </span>
              <span
                className={`hud-target ${contractTeam === "B" ? "is-contract" : ""}`}
              >
                B: push {contractTeam === "B" ? B : T - B} / win{" "}
                {contractTeam === "B" ? B + 1 : T - B + 1}
              </span>
            </>
          ) : (
            <span className="hud-target">awaiting bid</span>
          )}
        </span>
      </div>

      <div className="hud-block hud-score" aria-label="Hands won" aria-live="polite" aria-atomic="true">
        <span className="hud-label">Hands</span>
        <span className="hud-value hud-score-value">
          <span className={`hud-score-pill ${contractTeam === "A" ? "is-contract" : ""}`}>
            A <strong>{tricksA}</strong>
          </span>
          <span className={`hud-score-pill ${contractTeam === "B" ? "is-contract" : ""}`}>
            B <strong>{tricksB}</strong>
          </span>
        </span>
      </div>
    </div>
  );
}
